import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import CurrencySymbol, { currencySymbolHtml } from "../../Components/common/CurrencySymbol";
import DashboardTable from "./DashboardTable";
import { loadFleetBusiness, pointsVersusPreviousMonth } from "./fleetApi";
import {
  formatCount,
  formatRevenue,
  formatSignedPct,
  loadWorkshopBusiness,
  shareOf,
  vsAvg,
  vsPrev,
  WORKSHOP_STATION_CODES,
} from "./workshopApi";
import "./businessDashboard.scss";
import { GlobalStyles } from "@mui/material";
import { dashMainViewScrollbarStyles } from "../shared/dashboardEnterprise";

const COLORS = {
  workshop: "#00C179",
  fleet: "#3B82F6",
  sianty: "#8B5CF6",
  previous: "#94A3B8",
  avg: "#CBD5E1",
  warning: "#F59E0B",
};

const PERIODS = [
  "Today",
  "MTD",
  "Current Week",
  "Last Week",
  "Last Month",
  "YTD",
  "Last Year",
  "All",
];

const BUSINESSES = [
  "All Businesses",
  "Workshops",
  "Bayanat FleetOS",
  // "Saas",
];

const INTERVAL_LABELS = {
  Today: "Today • current day + comparison",
  MTD: "MTD • current month to date + comparison",
  "Current Week": "Current Week • current calendar week + comparison",
  "Last Week": "Last Week • previous calendar week + comparison",
  "Last Month": "Last Month • previous calendar month + comparison",
  YTD: "YTD • current year to date + comparison",
  "Last Year": "Last Year • previous full year + comparison",
  All: "All • full available history + comparison",
};

const chartBase = {
  credits: { enabled: false },
  title: { text: undefined },
  chart: {
    backgroundColor: "transparent",
    style: { fontFamily: "'Open Sans', sans-serif" },
  },
  legend: {
    itemStyle: { color: "#4b5563", fontSize: "11px", fontWeight: "500" },
  },
};

const exactAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const Money = ({ amount, currency }) => (
  <span className="bd-money">
    <CurrencySymbol currency={currency} withParentheses={false} />
    {amount}
  </span>
);

const Amount = ({ currency, text, value }) => {
  if (!text || text === "—" || text === "…") return text || "—";
  const exact = exactAmount(value);
  return (
    <span className="bd-money" title={exact ? `${exact}` : undefined}>
      <CurrencySymbol currency={currency} withParentheses={false} />
      {text}
    </span>
  );
};

const chartPoint = (value) => (value == null || Number.isNaN(Number(value)) ? null : Number(value));

const actualMoneyAxis = (currency) => ({
  useHTML: true,
  formatter() {
    return `${currencySymbolHtml(currency)} ${formatRevenue(this.value)}`;
  },
});

const actualMoneyTooltip = (currency) => ({
  shared: true,
  useHTML: true,
  formatter() {
    const points = this.points || [this];
    const rows = points
      .map(
        (point) =>
          `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${currencySymbolHtml(currency)} ${exactAmount(point.y)}</b>`
      )
      .join("<br/>");
    return `<span style="font-size:11px">${this.x}</span><br/>${rows}`;
  },
});

const stackedTotalMoneyTooltip = (currency) => ({
  shared: true,
  useHTML: true,
  formatter() {
    const points = this.points || [this];
    const rows = points
      .map(
        (point) =>
          `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${currencySymbolHtml(currency)} ${exactAmount(point.y)}</b>`
      )
      .join("<br/>");
    const total = points.reduce((sum, point) => sum + (Number(point.y) || 0), 0);
    return `<span style="font-size:11px">${this.x}</span><br/>${rows}<br/><b>Total: ${currencySymbolHtml(currency)} ${exactAmount(total)}</b>`;
  },
});

const moneyAxisLabels = (currency) => ({
  useHTML: true,
  formatter() {
    return `${currencySymbolHtml(currency)} ${this.value}K`;
  },
});

const moneyTooltip = (currency, shared = true) => ({
  shared,
  useHTML: true,
  headerFormat: '<span style="font-size:11px">{point.key}</span><br/>',
  pointFormat: `<span style="color:{series.color}">\u25CF</span> {series.name}: <b>${currencySymbolHtml(currency)} {point.y}K</b><br/>`,
});

const WidgetInfo = ({ title }) => {
  if (!title) return null;
  return (
    <Tooltip title={<span style={{ fontSize: 12 }}>{title}</span>} arrow enterDelay={200} placement="top">
      <span className="bd-info" aria-label={title}>
        <InfoOutlinedIcon className="bd-info-icon" />
      </span>
    </Tooltip>
  );
};

const WidgetLabel = ({ children, info }) => (
  <div className="bd-label-row">
    <p className="bd-label">{children}</p>
    <WidgetInfo title={info || children} />
  </div>
);

const KpiCard = ({
  label,
  info,
  value,
  accent,
  valueTone,
  vsPrev,
  vsPrevTone,
  vsAvg,
  note,
  className = "",
  children,
}) => (
  <div className={`bd-card accent ${accent ? `${accent}-border` : ""} ${className}`}>
    <WidgetLabel info={info}>{label}</WidgetLabel>
    <p className={`bd-value ${valueTone || accent || ""}`}>{value}</p>
    {(vsPrev || vsAvg) && (
      <div className="bd-meta">
        {vsPrev && <span className={vsPrevTone || "bd-up"}>{vsPrev}</span>}
        {vsAvg && <span className="bd-muted">{vsAvg}</span>}
      </div>
    )}
    {note && <p className="bd-note" style={{ marginTop: 6 }}>{note}</p>}
    {children}
  </div>
);

const SectionHead = ({ eyebrow, title, subtitle, aside, tone, actions }) => (
  <div className="bd-section-head">
    <div>
      <p className={`bd-eyebrow ${tone || ""}`}>{eyebrow}</p>
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
    {(aside || actions) && (
      <div className="bd-section-head-right">
        {actions}
        {aside && <span className={`bd-aside ${tone || ""}`}>{aside}</span>}
      </div>
    )}
  </div>
);

const ProgressRow = ({ label, value, percent, tone = "fleet", extra }) => (
  <div className="bd-progress">
    <div className="bd-progress-row">
      <span>{label}</span>
      <b>
        {value}
        {extra}
      </b>
    </div>
    <div className={`bd-bar ${tone}`}>
      <span style={{ width: `${percent}%` }} />
    </div>
  </div>
);

const BusinessDashboardView = () => {
  const [period, setPeriod] = useState("YTD");
  const [business, setBusiness] = useState("All Businesses");
  const [filterByStationCode, setFilterByStationCode] = useState(true);
  const [workshop, setWorkshop] = useState(null);
  const [workshopLoading, setWorkshopLoading] = useState(true);
  const [fleet, setFleet] = useState(null);
  const [fleetLoading, setFleetLoading] = useState(true);
  const logInUser = useSelector((state) => state.logInUsers);
  const currency = logInUser?.attributes?.currencyLabel || "AED";
  const timezone = logInUser?.attributes?.timezone || "Asia/Riyadh";

  useEffect(() => {
    let active = true;
    setWorkshopLoading(true);
    loadWorkshopBusiness(timezone, period, { filterByStationCode })
      .then((data) => {
        if (!active) return;
        setWorkshop(data);
        setWorkshopLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setWorkshopLoading(false);
      });
    return () => {
      active = false;
    };
  }, [timezone, period, filterByStationCode]);

  useEffect(() => {
    let active = true;
    setFleetLoading(true);
    loadFleetBusiness(timezone, period)
      .then((data) => {
        if (!active) return;
        setFleet(data);
        setFleetLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setFleetLoading(false);
      });
    return () => {
      active = false;
    };
  }, [timezone, period]);

  const showWorkshops = business === "All Businesses" || business === "Workshops";
  const showFleet = business === "All Businesses" || business === "Bayanat FleetOS";
  // const showSianty = business === "All Businesses" || business === "Saas";

  const fleetAccountColumns = useMemo(() => {
    const revenue = (value) => <Amount currency={currency} text={formatRevenue(value)} value={value} />;
    return [
      {
        key: "account",
        label: "Fleet Account",
        bold: true,
        render: (row) => row.account,
        searchText: (row) =>
          [row.account, row.accountName, row.contractNumber].filter(Boolean).join(" "),
        sortValue: (row) => row.accountName || row.account,
      },
      { key: "vehicles", label: "Vehicles", render: (row) => formatCount(row.vehicles) },
      { key: "serviced", label: "Serviced", render: (row) => formatCount(row.serviced) },
      { key: "fleetOsRevenue", label: "FleetOS Rev.", render: (row) => revenue(row.fleetOsRevenue) },
      { key: "workshopRevenue", label: "Workshop Rev.", tone: "bd-up", render: (row) => revenue(row.workshopRevenue) },
      {
        key: "servicePenetration",
        label: "Service Pen.",
        render: (row) => (row.servicePenetration == null ? "—" : `${Number(row.servicePenetration).toFixed(1)}%`),
      },
      {
        key: "status",
        label: "Status",
        sortable: false,
        tone: (row) => row.status?.tone || "",
        searchText: (row) => row.status?.label || "",
        render: (row) => row.status?.label || "—",
      },
    ];
  }, [currency]);

  const branchColumns = useMemo(() => {
    const revenue = (value) => <Amount currency={currency} text={formatRevenue(value)} value={value} />;
    return [
      { key: "name", label: "Workshop", bold: true, render: (row) => row.name },
      { key: "total", label: "Total", bold: true, render: (row) => revenue(row.total) },
      { key: "walkin", label: "Walk-in", render: (row) => revenue(row.walkin) },
      { key: "fleet", label: "Fleet", tone: "bd-fleet", render: (row) => revenue(row.fleet) },
      {
        key: "growth",
        label: "Growth",
        tone: (row) => (row.growth > 0 ? "bd-up" : row.growth < 0 ? "bd-down" : ""),
        render: (row) => formatSignedPct(row.growth),
      },
      { key: "walkinTicket", label: "Walk-in Ticket", render: (row) => <Amount currency={currency} text={formatCount(row.walkinTicket)} value={row.walkinTicket} /> },
      { key: "fleetTicket", label: "Fleet Ticket", render: (row) => <Amount currency={currency} text={formatCount(row.fleetTicket)} value={row.fleetTicket} /> },
      {
        key: "margin",
        label: "Margin",
        tone: (row) => (row.margin == null ? "" : row.margin >= 40 ? "bd-up" : "bd-warning"),
        render: (row) => (row.margin == null ? "—" : `${Number(row.margin).toFixed(1)}%`),
      },
    ];
  }, [currency]);

  const currentFleet = fleet?.current;
  const previousFleet = fleet?.previous;
  const fleetRevenueChange = fleetLoading ? null : vsPrev(currentFleet?.fleetOsRevenue, previousFleet?.fleetOsRevenue, false);
  const fleetRevenueVersusLastMonth = fleetRevenueChange
    ? { text: `${fleetRevenueChange.text} vs last month`, tone: fleetRevenueChange.tone }
    : null;
  const lastMonthFleetRevenue = fleetLoading || previousFleet?.fleetOsRevenue == null
    ? null
    : formatRevenue(previousFleet.fleetOsRevenue);
  const managedVehiclesVersusPrevious = fleetLoading ? null : vsPrev(currentFleet?.managedVehicles, previousFleet?.managedVehicles);
  const coverageVersusPrevious = fleetLoading ? null : pointsVersusPreviousMonth(currentFleet?.activeCoverage, previousFleet?.activeCoverage);
  const yieldVersusPrevious = fleetLoading ? null : pointsVersusPreviousMonth(currentFleet?.workshopYield, previousFleet?.workshopYield);

  const vehicleCountWhileLoading = (value) => (fleetLoading ? "…" : formatCount(value));
  const moneyWhileLoading = (value) => (fleetLoading ? "…" : formatRevenue(value));
  const percentWhileLoading = (value) => (fleetLoading ? "…" : value == null ? "—" : `${Number(value).toFixed(1)}%`);
  const deliveryNotesLabel = (count) => {
    if (count == null) return "Generated through workshops";
    const n = Math.round(Number(count));
    return `${n.toLocaleString()} delivery ${n === 1 ? "note" : "notes"}`;
  };

  const handlePrint = () => {
    const reflowCharts = () => {
      (Highcharts.charts || []).forEach((chart) => {
        if (chart && typeof chart.reflow === "function") {
          chart.reflow();
        }
      });
    };

    const pageStyle = document.createElement("style");
    pageStyle.setAttribute("data-bd-print-page", "true");
    pageStyle.textContent = "@page { size: A4 landscape; margin: 8mm; }";
    document.head.appendChild(pageStyle);

    const cleanup = () => {
      document.body.classList.remove("bd-print-mode");
      pageStyle.remove();
      window.removeEventListener("afterprint", cleanup);
      reflowCharts();
    };

    document.body.classList.add("bd-print-mode");
    reflowCharts();
    window.addEventListener("afterprint", cleanup);
    window.setTimeout(() => window.print(), 200);
  };

  useEffect(() => {
    return () => {
      document.body.classList.remove("bd-print-mode");
      document.querySelector("[data-bd-print-page]")?.remove();
    };
  }, []);

  const workshopCompareOptions = useMemo(
    () => ({
      ...chartBase,
      chart: { ...chartBase.chart, type: "column", height: 250 },
      xAxis: {
        categories: ["Total", "Walk-in", "Fleet"],
        lineColor: "#e2e9ef",
      },
      yAxis: {
        title: { text: null },
        gridLineColor: "#eef2f6",
        min: 0,
        labels: actualMoneyAxis(currency),
      },
      plotOptions: {
        column: { borderWidth: 0, borderRadius: 3, groupPadding: 0.15 },
      },
      tooltip: actualMoneyTooltip(currency),
      series: [
        {
          name: "Current",
          data: [
            chartPoint(workshop?.comparisonCurrent?.total),
            chartPoint(workshop?.comparisonCurrent?.walkin),
            chartPoint(workshop?.comparisonCurrent?.fleet),
          ],
          color: COLORS.workshop,
        },
        {
          name: "Previous",
          data: [
            chartPoint(workshop?.comparisonPrevious?.total),
            chartPoint(workshop?.comparisonPrevious?.walkin),
            chartPoint(workshop?.comparisonPrevious?.fleet),
          ],
          color: COLORS.previous,
        },
        {
          name: "Historical Avg",
          data: [
            chartPoint(workshop?.historyAverage?.total),
            chartPoint(workshop?.historyAverage?.walkin),
            chartPoint(workshop?.historyAverage?.fleet),
          ],
          color: COLORS.avg,
        },
      ],
    }),
    [currency, workshop]
  );

  const workshopTrendOptions = useMemo(
    () => ({
      ...chartBase,
      chart: { ...chartBase.chart, type: "spline", height: 250 },
      xAxis: {
        categories: (workshop?.months || []).map((month) => month.label),
        lineColor: "#e2e9ef",
      },
      yAxis: {
        title: { text: null },
        gridLineColor: "#eef2f6",
        min: 0,
        labels: actualMoneyAxis(currency),
      },
      tooltip: actualMoneyTooltip(currency),
      plotOptions: {
        spline: { marker: { radius: 3 } },
      },
      series: [
        {
          name: "Total",
          data: (workshop?.months || []).map((month) => chartPoint(month.total)),
          color: "#0f172a",
        },
        {
          name: "Walk-in",
          data: (workshop?.months || []).map((month) => chartPoint(month.walkin)),
          color: COLORS.workshop,
        },
        {
          name: "Fleet",
          data: (workshop?.months || []).map((month) => chartPoint(month.fleet)),
          color: COLORS.fleet,
        },
      ],
    }),
    [currency, workshop]
  );

  const siantyTrendOptions = useMemo(
    () => ({
      ...chartBase,
      chart: { ...chartBase.chart, type: "spline", height: 250 },
      legend: { enabled: false },
      xAxis: {
        categories: ["Apr", "May", "Jun", "Jul", "Aug", "Sep"],
        lineColor: "#e2e9ef",
      },
      yAxis: {
        title: { text: null },
        gridLineColor: "#eef2f6",
        labels: moneyAxisLabels(currency),
      },
      tooltip: moneyTooltip(currency, false),
      series: [
        { name: "MRC", data: [58, 62, 67, 72, 77, 82], color: COLORS.sianty },
      ],
    }),
    [currency]
  );

  const siantyMixOptions = useMemo(
    () => ({
      ...chartBase,
      chart: { ...chartBase.chart, type: "pie", height: 250 },
      tooltip: { pointFormat: "<b>{point.percentage:.1f}%</b>" },
      plotOptions: {
        pie: {
          innerSize: "65%",
          borderWidth: 0,
          dataLabels: { enabled: false },
        },
      },
      series: [
        {
          name: "Revenue",
          data: [
            { name: "MRC", y: 82, color: COLORS.sianty },
            { name: "Other Saas Revenue", y: 23, color: "#94A3B8" },
          ],
        },
      ],
    }),
    []
  );

  const cash = workshop?.current;
  const cashPrev = workshop?.previous;
  const hist = workshop?.historyAverage;
  const jobs = workshop?.jobs;
  const jobsPrev = workshop?.jobsPrev;
  const tickets = workshop?.tickets;
  const ticketsPrev = workshop?.ticketsPrev;
  const revenueText = (amount) => (workshopLoading ? "…" : formatRevenue(amount));
  const countText = (amount) => (workshopLoading ? "…" : formatCount(amount));
  const totalPrev = workshopLoading ? null : vsPrev(cash?.total, cashPrev?.total, true);
  const walkinPrev = workshopLoading ? null : vsPrev(cash?.walkin, cashPrev?.walkin, true);
  const fleetPrev = workshopLoading ? null : vsPrev(cash?.fleet, cashPrev?.fleet, true);
  const walkinJobsDelta = workshopLoading ? null : vsPrev(jobs?.walkin, jobsPrev?.walkin);
  const fleetJobsDelta = workshopLoading ? null : vsPrev(jobs?.fleet, jobsPrev?.fleet);
  const walkinTicketDelta = workshopLoading ? null : vsPrev(tickets?.walkin, ticketsPrev?.walkin);
  const fleetTicketDelta = workshopLoading ? null : vsPrev(tickets?.fleet, ticketsPrev?.fleet);
  const jobNote = (count) => {
    if (workshopLoading) return " ";
    if (count == null) return "—";
    return `${formatCount(count)} jobs`;
  };

  const groupLoading = workshopLoading || fleetLoading;
  const workshopsRevenue = cash?.total;
  const fleetOsRevenue = currentFleet?.fleetOsRevenue;
  const groupRevenue =
    workshopsRevenue == null && fleetOsRevenue == null
      ? null
      : (workshopsRevenue || 0) + (fleetOsRevenue || 0);
  const previousGroupRevenue =
    cashPrev?.total == null && previousFleet?.fleetOsRevenue == null
      ? null
      : (cashPrev?.total || 0) + (previousFleet?.fleetOsRevenue || 0);
  const groupPrev = groupLoading ? null : vsPrev(groupRevenue, previousGroupRevenue, true);
  const workshopsGroupPrev = groupLoading ? null : vsPrev(workshopsRevenue, cashPrev?.total, true);
  const fleetGroupPrev = groupLoading ? null : vsPrev(fleetOsRevenue, previousFleet?.fleetOsRevenue, true);
  const workshopsGroupAvg = groupLoading ? null : vsAvg(workshopsRevenue, hist?.total);
  const groupTotalShare = groupRevenue || 0;
  const workshopsShare = groupTotalShare > 0 && workshopsRevenue != null
    ? (workshopsRevenue / groupTotalShare) * 100
    : null;
  const fleetShare = groupTotalShare > 0 && fleetOsRevenue != null
    ? (fleetOsRevenue / groupTotalShare) * 100
    : null;
  const groupMoneyText = (amount) => (groupLoading ? "…" : formatRevenue(amount));

  const groupTrendOptions = useMemo(() => {
    const months = workshop?.months || [];
    const fleetByLabel = new Map(
      (fleet?.months || []).map((month) => [month.label, month.fleetOsRevenue]),
    );
    const fleetSeries = months.map((month, index) => {
      if (fleetByLabel.has(month.label)) {
        return chartPoint(fleetByLabel.get(month.label));
      }
      // Fallback when fleet months are not loaded yet: last two period totals.
      if (index === months.length - 1) return chartPoint(fleetOsRevenue);
      if (index === months.length - 2) return chartPoint(previousFleet?.fleetOsRevenue);
      return null;
    });
    return {
      ...chartBase,
      chart: { ...chartBase.chart, type: "column", height: 280 },
      xAxis: {
        categories: months.map((month) => month.label),
        lineColor: "#e2e9ef",
        tickColor: "#e2e9ef",
      },
      yAxis: {
        title: { text: null },
        gridLineColor: "#eef2f6",
        stackLabels: { enabled: false },
        min: 0,
        labels: actualMoneyAxis(currency),
      },
      tooltip: stackedTotalMoneyTooltip(currency),
      plotOptions: {
        column: {
          stacking: "normal",
          borderWidth: 0,
          borderRadius: 3,
        },
      },
      series: [
        {
          name: "Workshops",
          data: months.map((month) => chartPoint(month.total)),
          color: COLORS.workshop,
        },
        {
          name: "Bayanat FleetOS",
          data: fleetSeries,
          color: COLORS.fleet,
        },
      ],
    };
  }, [currency, workshop, fleet, fleetOsRevenue, previousFleet]);

  const groupMixOptions = useMemo(() => {
    const data = [];
    if (workshopsRevenue != null && workshopsRevenue !== 0) {
      data.push({ name: "Workshops", y: workshopsRevenue, color: COLORS.workshop });
    }
    if (fleetOsRevenue != null && fleetOsRevenue !== 0) {
      data.push({ name: "Bayanat FleetOS", y: fleetOsRevenue, color: COLORS.fleet });
    }
    return {
      ...chartBase,
      chart: { ...chartBase.chart, type: "pie", height: 210 },
      tooltip: {
        useHTML: true,
        pointFormatter() {
          return `<b>${currencySymbolHtml(currency)} ${exactAmount(this.y)}</b><br/>${this.percentage.toFixed(1)}%`;
        },
      },
      plotOptions: {
        pie: {
          innerSize: "68%",
          borderWidth: 0,
          dataLabels: { enabled: false },
        },
      },
      legend: { enabled: false },
      series: [{ name: "Revenue", data }],
    };
  }, [currency, workshopsRevenue, fleetOsRevenue]);

  return (
    <div className="business-dashboard">
      <GlobalStyles styles={dashMainViewScrollbarStyles} />
      <div className="bd-toolbar">
        <div className="bd-toolbar-left">
          <div>
            <h1>Executive Revenue & Business Intelligence</h1>
            <p>Workshops • Bayanat FleetOS</p>
          </div>
        </div>
        <div className="bd-toolbar-actions">
          <select
            className="bd-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            aria-label="Reporting interval"
          >
            {PERIODS.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <select
            className="bd-select"
            value={business}
            onChange={(e) => setBusiness(e.target.value)}
            aria-label="Business filter"
          >
            {BUSINESSES.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <button className="bd-print-btn" type="button" onClick={handlePrint}>
            Print / PDF
          </button>
        </div>
      </div>

      <section className="bd-section">
        <SectionHead
          eyebrow="Group Overview"
          title="Revenue across the two businesses"
          aside={INTERVAL_LABELS[period]}
        />
        <div className="bd-grid bd-grid-3">
          <KpiCard
            label="Total Group Revenue"
            info="Workshops revenue plus Bayanat FleetOS revenue for the selected period."
            value={<Amount currency={currency} text={groupMoneyText(groupRevenue)} value={groupRevenue} />}
            vsPrev={groupPrev?.text}
            vsPrevTone={groupPrev?.tone}
          />
          <KpiCard
            label="Workshops Revenue"
            info="Total workshop cash revenue from walk-in and fleet channels."
            value={<Amount currency={currency} text={groupMoneyText(workshopsRevenue)} value={workshopsRevenue} />}
            accent="workshop"
            vsPrev={workshopsGroupPrev?.text}
            vsPrevTone={workshopsGroupPrev?.tone}
            vsAvg={workshopsGroupAvg}
          />
          <KpiCard
            label="Bayanat FleetOS Revenue"
            info="FleetOS sale net amount from the fleet delivery-note total for the selected period."
            value={<Amount currency={currency} text={groupMoneyText(fleetOsRevenue)} value={fleetOsRevenue} />}
            accent="fleet"
            vsPrev={fleetGroupPrev?.text}
            vsPrevTone={fleetGroupPrev?.tone}
          />
        </div>
      </section>

      <section className="bd-section">
        <div className="bd-grid bd-trend-grid">
          <div className="bd-card">
            <WidgetLabel info="Monthly stacked revenue for Workshops and Bayanat FleetOS. YTD shows Jan through the current month; Last Year shows the full previous year.">
              Revenue Trend
            </WidgetLabel>
            <h2>Workshop and FleetOS revenue trend</h2>
            <HighchartsReact highcharts={Highcharts} options={groupTrendOptions} />
          </div>
          <div className="bd-card">
            <WidgetLabel info="Share of current-period revenue between Workshops and Bayanat FleetOS.">
              Revenue Mix
            </WidgetLabel>
            <h2>Current period</h2>
            <HighchartsReact highcharts={Highcharts} options={groupMixOptions} />
            <div className="bd-mix-legend">
              <div>
                <span className="bd-up">● Workshops</span>
                <b>{groupLoading || workshopsShare == null ? "…" : `${workshopsShare.toFixed(1)}%`}</b>
              </div>
              <div>
                <span style={{ color: COLORS.fleet }}>● Bayanat FleetOS</span>
                <b>{groupLoading || fleetShare == null ? "…" : `${fleetShare.toFixed(1)}%`}</b>
              </div>
            </div>
          </div>
        </div>
      </section>
      {showFleet && (
        <>
          <section className="bd-section">
            <SectionHead
              eyebrow="Business 01"
              title="Bayanat FleetOS"
              subtitle="Fleet business performance and the workshop revenue generated by that fleet."
              aside="Fleet → Workshop engine"
              tone="fleet"
            />
            <div className="bd-fleet-kpis">
              <KpiCard
                className="bd-kpi"
                label="FleetOS Revenue"
                info="FleetOS revenue from sale net amount and invoice sale net amount for the selected period."
                value={<Amount currency={currency} text={fleetLoading ? "…" : formatRevenue(currentFleet?.fleetOsRevenue)} value={currentFleet?.fleetOsRevenue} />}
                accent="fleet"
                vsPrev={fleetRevenueVersusLastMonth?.text}
                vsPrevTone={fleetRevenueVersusLastMonth?.tone}
                note={lastMonthFleetRevenue == null ? null : (
                  <>Last month <Amount currency={currency} text={lastMonthFleetRevenue} value={previousFleet?.fleetOsRevenue} /></>
                )}
              />
              <KpiCard
                className="bd-kpi"
                label="Managed Vehicles"
                info="Total fleet vehicles managed in FleetOS for the selected period."
                value={vehicleCountWhileLoading(currentFleet?.managedVehicles)}
                vsPrev={managedVehiclesVersusPrevious?.text}
                vsPrevTone={managedVehiclesVersusPrevious?.tone}
              />
              {/* <KpiCard
                className="bd-kpi"
                label="Active Fleet"
                value={vehicleCountWhileLoading(currentFleet?.activeFleet)}
                valueTone="fleet"
                note={fleetLoading ? " " : `${percentWhileLoading(currentFleet?.activeCoverage)} of managed`}
              /> */}
              <KpiCard
                className="bd-kpi"
                label="Serviced Vehicles"
                info="Managed vehicles that received at least one workshop job in the selected period."
                value={vehicleCountWhileLoading(currentFleet?.servedVehicles)}
                valueTone="workshop"
                note={fleetLoading ? " " : `${percentWhileLoading(currentFleet?.servicePenetration)} of fleet`}
              />
              <KpiCard
                className="bd-kpi"
                label="Service Penetration"
                info="Serviced vehicles divided by managed vehicles."
                value={percentWhileLoading(currentFleet?.servicePenetration)}
                note="Serviced ÷ fleet"
              />
              <KpiCard
                className="bd-kpi"
                label="Workshop Revenue from Fleet"
                info="Workshop delivery-note net amount generated by fleet vehicles."
                value={<Amount currency={currency} text={moneyWhileLoading(currentFleet?.workshopRevenue)} value={currentFleet?.workshopRevenue} />}
                valueTone="workshop"
                note={fleetLoading ? " " : deliveryNotesLabel(currentFleet?.deliveryNotes)}
              />
              <KpiCard
                className="bd-kpi"
                label="Fleet AR"
                info="Outstanding fleet invoice balance (accounts receivable)."
                value={<Amount currency={currency} text={moneyWhileLoading(currentFleet?.invoiceBalance)} value={currentFleet?.invoiceBalance} />}
                valueTone="warning"
                note="Invoice balance"
              />
            </div>

            <div className="bd-grid bd-grid-2" style={{ marginTop: 16 }}>
              {/* <div className="bd-card bd-stat-row">
                <div>
                  <p className="bd-label">Active Fleet Coverage</p>
                  <p className="bd-sub">Active ÷ Managed</p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p className="bd-value fleet">{percentWhileLoading(currentFleet?.activeCoverage)}</p>
                  {coverageVersusPrevious && <p className={coverageVersusPrevious.tone} style={{ fontSize: 10 }}>{coverageVersusPrevious.text}</p>}
                </div>
              </div> */}
              <div className="bd-card bd-stat-row">
                <div>
                  <WidgetLabel info="Workshop revenue from fleet divided by FleetOS revenue.">
                    Fleet Workshop Yield
                  </WidgetLabel>
                  <p className="bd-sub">Workshop revenue ÷ FleetOS revenue</p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p className="bd-value workshop">{percentWhileLoading(currentFleet?.workshopYield)}</p>
                  {yieldVersusPrevious && <p className={yieldVersusPrevious.tone} style={{ fontSize: 10 }}>{yieldVersusPrevious.text}</p>}
                </div>
              </div>
              <div className="bd-card bd-stat-row">
                <div>
                  <WidgetLabel info="Managed vehicles that did not receive a workshop job in the selected period.">
                    Unserviced Active Fleet
                  </WidgetLabel>
                  <p className="bd-sub">Vehicles without a workshop job</p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p className="bd-value warning">{vehicleCountWhileLoading(currentFleet?.unservedVehicles)}</p>
                  <p className="bd-warning" style={{ fontSize: 10 }}>{fleetLoading ? " " : `${percentWhileLoading(currentFleet?.unservedShare)} of fleet`}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="bd-section">
            <div className="bd-grid bd-grid-1">
              {/* <div className="bd-card">
                <p className="bd-label">Fleet → Workshop Conversion</p>
                <h2>How FleetOS feeds workshop revenue</h2>
                <ProgressRow label="Managed Fleet" value="3,000" percent={100} tone="fleet" />
                <ProgressRow label="Active Fleet" value="2,640" percent={88} tone="fleet" />
                <ProgressRow label="Serviced Through Workshops" value="1,940" percent={64.7} tone="workshop" />
                <div className="bd-nested-grid">
                  <div className="bd-nested">
                    <p className="bd-label">Service Penetration</p>
                    <p className="bd-value workshop">73.5%</p>
                  </div>
                  <div className="bd-nested">
                    <p className="bd-label">Workshop Fleet Revenue</p>
                    <p className="bd-value workshop"><Money currency={currency} amount="249K" /></p>
                  </div>
                </div>
                <div className="bd-insight fleet">
                  <b className="fleet">Management question:</b> Why are 700 active vehicles not yet serviced during the period, and how much workshop revenue could they represent?
                </div>
              </div> */}

              <div className="bd-card">
                <WidgetLabel info="City-wise share of workshop sales from fleet and walk-in channels.">
                  Regional Fleet Penetration
                </WidgetLabel>
                <h2>Where the fleet business is growing</h2>
                {workshopLoading && <p className="bd-note">…</p>}
                {!workshopLoading && (workshop?.regions || []).length === 0 && <p className="bd-note">—</p>}
                {!workshopLoading && (workshop?.regions || []).map((row) => (
                  <ProgressRow
                    key={row.name}
                    label={row.name}
                    value={<span style={{ color: COLORS.fleet }}>{row.share.toFixed(1)}%</span>}
                    extra={row.change == null ? null : (
                      <span className={row.change < 0 ? "bd-down" : "bd-up"} style={{ fontSize: 10 }}>
                        {` ${row.change > 0 ? "+" : ""}${row.change.toFixed(1)}%`}
                      </span>
                    )}
                    percent={row.share}
                    tone="fleet"
                  />
                ))}
              </div>
            </div>
          </section>

          <section className="bd-section">
            <div className="bd-card">
              <DashboardTable
                header={(
                  <>
                    <WidgetLabel info="Fleet accounts with vehicles, service penetration, FleetOS revenue, and workshop revenue for the current month.">
                      Fleet Customer Performance
                    </WidgetLabel>
                    <h2>Fleet accounts driving revenue and workshop demand</h2>
                  </>
                )}
                columns={fleetAccountColumns}
                rows={fleet?.accounts || []}
                loading={fleetLoading}
                maxHeight={251}
                defaultSort={{ key: "fleetOsRevenue", direction: "desc" }}
                searchPlaceholder="Search account or contract #"
                rowKey={(row, index) => `${row.account}-${row.contractNumber || index}`}
              />
            </div>
          </section>
        </>
      )}
      {showWorkshops && (
        <>
          <section className="bd-section">
            <SectionHead
              eyebrow="Business 02"
              title="Workshop Revenue & Operations"
              subtitle="Walk-in and Fleet workshop revenue are measured separately."
              aside="Core operating business"
              tone="workshop"
              actions={
                <label
                  className={`bd-switch ${filterByStationCode ? "is-on" : ""}`}
                  title={`When enabled, Workshop APIs filter by station codes ${WORKSHOP_STATION_CODES.join(", ")}`}
                >
                  <input
                    type="checkbox"
                    checked={filterByStationCode}
                    onChange={(e) => setFilterByStationCode(e.target.checked)}
                    aria-label="Filter Workshop by station code"
                  />
                  <span className="bd-switch-track" aria-hidden="true">
                    <span className="bd-switch-thumb" />
                  </span>
                  <span className="bd-switch-text">OBD Workshop</span>
                </label>
              }
            />
            <div className="bd-workshop-kpis">
              <KpiCard
                className="bd-kpi"
                label="Total Revenue"
                info="Walk-in plus fleet workshop cash revenue for the selected period."
                value={<Amount currency={currency} text={revenueText(cash?.total)} value={cash?.total} />}
                accent="workshop"
                vsPrev={totalPrev?.text}
                vsPrevTone={totalPrev?.tone}
                vsAvg={workshopLoading ? null : vsAvg(cash?.total, hist?.total)}
              />
              <KpiCard
                className="bd-kpi"
                label="Walk-in Revenue"
                info="Workshop cash revenue from walk-in / bfmc005 channel."
                value={<Amount currency={currency} text={revenueText(cash?.walkin)} value={cash?.walkin} />}
                vsPrev={walkinPrev?.text}
                vsPrevTone={walkinPrev?.tone}
                vsAvg={workshopLoading ? null : vsAvg(cash?.walkin, hist?.walkin)}
              />
              <KpiCard
                className="bd-kpi"
                label="Fleet Revenue"
                info="Workshop cash revenue from the fleet channel."
                value={<Amount currency={currency} text={revenueText(cash?.fleet)} value={cash?.fleet} />}
                valueTone="fleet"
                vsPrev={fleetPrev?.text}
                vsPrevTone={fleetPrev?.tone}
                vsAvg={workshopLoading ? null : vsAvg(cash?.fleet, hist?.fleet)}
              />

              <div className="bd-card bd-kpi bd-span-2">
                <div className="bd-nested-head">
                  <div>
                    <WidgetLabel info="Sum of delivery-note counts by city for walk-in and fleet channels.">
                      Workshop Jobs
                    </WidgetLabel>
                    <p className="bd-sub">Total jobs with Walk-in and Fleet breakdown</p>
                  </div>
                  <div className="bd-pill">
                    <span>Total Jobs</span>
                    <b>{countText(jobs?.total)}</b>
                  </div>
                </div>
                <div className="bd-nested-grid">
                  <div className="bd-nested">
                    <div className="bd-nested-row">
                      <p className="bd-label">Walk-in</p>
                      {walkinJobsDelta && <span className={walkinJobsDelta.tone}>{walkinJobsDelta.text}</span>}
                    </div>
                    <p className="bd-value">{countText(jobs?.walkin)}</p>
                    <p className="bd-note">{workshopLoading ? " " : shareOf(jobs?.walkin, jobs?.total)}</p>
                  </div>
                  <div className="bd-nested">
                    <div className="bd-nested-row">
                      <p className="bd-label">Fleet</p>
                      {fleetJobsDelta && <span className={fleetJobsDelta.tone}>{fleetJobsDelta.text}</span>}
                    </div>
                    <p className="bd-value fleet">{countText(jobs?.fleet)}</p>
                    <p className="bd-note">{workshopLoading ? " " : shareOf(jobs?.fleet, jobs?.total)}</p>
                  </div>
                </div>
              </div>

              <div className="bd-card bd-kpi bd-span-2">
                <div className="bd-nested-head">
                  <div>
                    <WidgetLabel info="Average of each city's average ticket where the value is above 0, split by walk-in and fleet.">
                      Average Ticket
                    </WidgetLabel>
                    <p className="bd-sub">Workshop average ticket by customer channel</p>
                  </div>
                  <div className="bd-pill">
                    <span>Combined Avg</span>
                    <b><Amount currency={currency} text={countText(tickets?.total)} value={tickets?.total} /></b>
                  </div>
                </div>
                <div className="bd-nested-grid">
                  <div className="bd-nested">
                    <div className="bd-nested-row">
                      <p className="bd-label">Walk-in</p>
                      {walkinTicketDelta && <span className={walkinTicketDelta.tone}>{walkinTicketDelta.text}</span>}
                    </div>
                    <p className="bd-value"><Amount currency={currency} text={countText(tickets?.walkin)} value={tickets?.walkin} /></p>
                    <p className="bd-note">{jobNote(jobs?.walkin)}</p>
                  </div>
                  <div className="bd-nested">
                    <div className="bd-nested-row">
                      <p className="bd-label">Fleet</p>
                      {fleetTicketDelta && <span className={fleetTicketDelta.tone}>{fleetTicketDelta.text}</span>}
                    </div>
                    <p className="bd-value fleet"><Amount currency={currency} text={countText(tickets?.fleet)} value={tickets?.fleet} /></p>
                    <p className="bd-note">{jobNote(jobs?.fleet)}</p>
                  </div>
                </div>
              </div>
            </div>
            {/* <div className="bd-note">
              Average Ticket is the average of each city's averageTicket where the value is above 0. Walk-in is bfmc005, Fleet is fleet, and Combined is both.
            </div> */}

            <div className="bd-grid bd-grid-2" style={{ marginTop: 24 }}>
              <div className="bd-card">
                <WidgetLabel info="Current, previous, and historical average workshop revenue for total, walk-in, and fleet. Comparison uses delivery note sale.">
                  Workshop Revenue Comparison
                </WidgetLabel>
                <h2>Current vs previous vs historical average</h2>
                <HighchartsReact highcharts={Highcharts} options={workshopCompareOptions} />
              </div>
              <div className="bd-card">
                <WidgetLabel info="Six-month trend of total, walk-in, and fleet workshop cash revenue.">
                  Workshop Revenue Trend
                </WidgetLabel>
                <h2>Total, Walk-in and Fleet</h2>
                <HighchartsReact highcharts={Highcharts} options={workshopTrendOptions} />
              </div>
            </div>
          </section>

          <section className="bd-section">
            <div className="bd-card">
              <DashboardTable
                header={(
                  <>
                    <WidgetLabel info="City / branch workshop performance with revenue, growth, ticket, and margin.">
                      Workshop Network
                    </WidgetLabel>
                    <h2>Branch performance</h2>
                  </>
                )}
                headerAside={<span className="bd-aside">Revenue growth + ticket + margin</span>}
                columns={branchColumns}
                rows={workshop?.branches || []}
                loading={workshopLoading}
                loadingLabel="Loading branch performance…"
                emptyLabel="No branch data for this month."
                defaultSort={{ key: "total", direction: "desc" }}
                searchPlaceholder="Search workshops"
                rowKey={(row, index) => `${row.name}-${index}`}
              />
            </div>
          </section>
        </>
      )}



      {/* {showSianty && (
        <section className="bd-section">
          <SectionHead
            eyebrow="Business 03"
            title="SaaS Revenue"
            subtitle="Pure software business metrics. No vehicle or fleet metrics."
            aside="Recurring revenue business"
            tone="sianty"
          />
          <div className="bd-grid bd-grid-5">
            <KpiCard label="Revenue" value={<Money currency={currency} amount="105K" />} accent="sianty" vsPrev="▲ 21.7% vs Prev" vsAvg="+16.6% vs Avg" />
            <KpiCard label="Growth" value="+21.7%" valueTone="workshop" note="vs previous period" />
            <KpiCard label="MRC" value={<Money currency={currency} amount="82K" />} note="Monthly Recurring Charge" />
            <KpiCard label="ARC" value={<Money currency={currency} amount="984K" />} note="Annual Recurring Charge run-rate" />
            <KpiCard label="Active Customers" value="70" note="Active WMS customers / sites" />
          </div>
          <div className="bd-grid bd-grid-2" style={{ marginTop: 24 }}>
            <div className="bd-card">
              <p className="bd-label">MRC Trend</p>
              <h2>Recurring revenue growth</h2>
              <HighchartsReact highcharts={Highcharts} options={siantyTrendOptions} />
            </div>
            <div className="bd-card">
              <p className="bd-label">Recurring Revenue Quality</p>
              <h2>MRC vs other revenue</h2>
              <HighchartsReact highcharts={Highcharts} options={siantyMixOptions} />
            </div>
          </div>
        </section>
      )}

      {showWorkshops && (
        <section className="bd-section">
          <SectionHead
            eyebrow="Workshop Intelligence"
            title="Demand, parts and vehicle intelligence"
            subtitle="Operational intelligence to forecast workshop demand and plan purchasing for the next three months."
            aside="AI / Analytics"
            tone="workshop"
          />
          <div className="bd-grid bd-grid-3">
            <div className="bd-card">
              <div className="bd-nested-head">
                <div>
                  <p className="bd-label">High-Demand Parts</p>
                  <h2>3-Month Consumption Forecast</h2>
                  <p className="bd-sub">Forecast based on historical workshop consumption</p>
                </div>
                <span className="bd-chip">Next 3 Months</span>
              </div>
              <div className="bd-month-row">
                <div className="bd-month"><p>Month 1</p><b>Oct</b></div>
                <div className="bd-month"><p>Month 2</p><b>Nov</b></div>
                <div className="bd-month"><p>Month 3</p><b>Dec</b></div>
              </div>
              {[
                {
                  name: "5W-30 Synthetic Engine Oil",
                  growth: "+17.8%",
                  tone: "workshop",
                  months: ["530 L", "548 L", "565 L"],
                  total: "1,643 L",
                },
                {
                  name: "Commercial Brake Pads",
                  growth: "+12.5%",
                  tone: "fleet",
                  months: ["135 sets", "141 sets", "147 sets"],
                  total: "423 sets",
                },
                {
                  name: "Heavy-Duty Filters",
                  growth: "+25.1%",
                  tone: "warning",
                  months: ["388", "406", "425"],
                  total: "1,219",
                },
              ].map((part) => (
                <div className="bd-part" key={part.name}>
                  <div className="bd-part-head">
                    <b>{part.name}</b>
                    <span className={part.tone === "warning" ? "bd-warning" : part.tone === "fleet" ? "" : "bd-up"} style={part.tone === "fleet" ? { color: COLORS.fleet, fontWeight: 700 } : { fontWeight: 700 }}>
                      {part.growth}
                    </span>
                  </div>
                  <div className="bd-part-grid">
                    <div><p>Oct</p><b>{part.months[0]}</b></div>
                    <div><p>Nov</p><b>{part.months[1]}</b></div>
                    <div><p>Dec</p><b>{part.months[2]}</b></div>
                    <div className="bd-part-total"><p>3-Month</p><b className="bd-up">{part.total}</b></div>
                  </div>
                </div>
              ))}
              <div className="bd-insight fleet">
                <b className="fleet">Planning insight:</b> Use the forecast to plan purchasing and supplier commitments for the next three months. This is a consumption forecast — not an inventory or stock-position report.
              </div>
            </div>

            <div className="bd-card">
              <p className="bd-label">Makes & Models</p>
              <h2>Vehicle mix driving workshop demand</h2>
              <ProgressRow label={<b>Toyota HiAce / Hilux</b>} value={<span style={{ color: COLORS.fleet }}>38%</span>} percent={38} tone="fleet" />
              <p className="bd-note">+5.2% growth • Commercial fleet leader</p>
              <ProgressRow label={<b>Nissan Patrol / Land Cruiser</b>} value={<span className="bd-up">26%</span>} percent={26} tone="workshop" />
              <p className="bd-note">+1.8% growth • Retail-heavy segment</p>
              <ProgressRow label={<b>Isuzu N-Series / Mitsubishi Fuso</b>} value={<span style={{ color: COLORS.sianty }}>18%</span>} percent={18} tone="sianty" />
              <p className="bd-note">+3.4% growth • Medium-duty commercial</p>
            </div>

            <div className="bd-card">
              <p className="bd-label">Most Demanded Services</p>
              <h2>What customers are asking for</h2>
              <ProgressRow label={<b>Periodic Maintenance</b>} value={<span className="bd-up">48%</span>} percent={48} tone="workshop" />
              <p className="bd-note">482 jobs • +4% vs previous</p>
              <ProgressRow label={<b>Brake & Suspension</b>} value={<span style={{ color: COLORS.fleet }}>28%</span>} percent={28} tone="fleet" />
              <p className="bd-note">281 jobs • +2% vs previous</p>
              <ProgressRow label={<b>AC & Electrical Diagnostics</b>} value={<span className="bd-warning">16%</span>} percent={16} tone="warning" />
              <p className="bd-note">161 jobs • Flat vs previous</p>
            </div>
          </div>
        </section>
      )}

      <section className="bd-section">
        <div className="bd-card">
          <p className="bd-label">Executive Scorecard</p>
          <h2>The three businesses at a glance</h2>
          <div className="bd-table-wrap" style={{ marginTop: 12 }}>
            <table className="bd-table">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Revenue</th>
                  <th>Growth vs Prev</th>
                  <th>Growth vs Avg</th>
                  <th>Primary KPI</th>
                  <th>Secondary KPI</th>
                  <th>Management Focus</th>
                </tr>
              </thead>
              <tbody>
                {showWorkshops && (
                  <tr>
                    <td className="bd-up"><b>Workshops</b></td>
                    <td><Money currency={currency} amount="535K" /></td>
                    <td className="bd-up">+11.4%</td>
                    <td className="bd-up">+8.2%</td>
                    <td>Walk-in + Fleet Revenue</td>
                    <td>AOV + Margin</td>
                    <td>Growth, capacity, margin</td>
                  </tr>
                )}
                {showFleet && (
                  <tr>
                    <td style={{ color: COLORS.fleet }}><b>Bayanat FleetOS</b></td>
                    <td><Money currency={currency} amount="410K" /></td>
                    <td className="bd-up">+19.2%</td>
                    <td className="bd-up">+14.5%</td>
                    <td>Managed / Active Fleet</td>
                    <td>Service Penetration</td>
                    <td>Convert fleet into workshop revenue</td>
                  </tr>
                )}
                {showSianty && (
                  <tr>
                    <td style={{ color: COLORS.sianty }}><b>Saas</b></td>
                    <td><Money currency={currency} amount="105K" /></td>
                    <td className="bd-up">+21.7%</td>
                    <td className="bd-up">+16.6%</td>
                    <td>MRC <Money currency={currency} amount="82K" /></td>
                    <td>ARC <Money currency={currency} amount="984K" /></td>
                    <td>Recurring revenue growth</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section> */}

      {/* <p className="bd-footer">
        Executive dashboard concept — sample figures shown for layout and KPI design. High-demand parts show forecast consumption only; no inventory/stock data is assumed. Connect to live accounting, workshop, FleetOS and Saas data before production.
      </p> */}
    </div>
  );
};

export default BusinessDashboardView;
